import type { FormEvent, JSX } from 'react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { estElevationAnnulee, useElevation } from '../../components/useElevation.js';
import { montantsRecette, repartirRecette, type ContexteRepartition } from '@artosera/shared';
import { Button, Input, Select } from '@artosera/ui';
import { creerEncaissement, modifierEncaissement, supprimerEncaissement } from '../../lib/api.js';
import {
  analyserMontant,
  analyserPourcentage,
  montantEnSaisie,
  pourcentageEnSaisie,
} from '../../lib/montant.js';
import type {
  BeneficiaireSaisie,
  Encaissement,
  GalerieListe,
  SaisieMontant,
  TypeRecetteVue,
  VersionGrille,
} from '../../types.js';
import { useMessageErreur } from '../Finances/commun.js';
import commun from '../VueDAigle/commun.module.css';
import { Section } from '../VueDAigle/composants.js';
import { useFormats } from '../VueDAigle/formats.js';
import styles from './Comptabilite.module.css';
import { useNomType } from './nomsTypes.js';
import { useEncaissements, useRelireComptabilite } from './useComptabilite.js';

/** Le jour d'aujourd'hui à Paris, `AAAA-MM-JJ`. */
function aujourdhui(): string {
  const parties = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const v = (type: string): string => parties.find((p) => p.type === type)?.value ?? '';
  return `${v('year')}-${v('month')}-${v('day')}`;
}

/** Le contexte de la répartition, tel que le serveur le lit : la grille et le partage. */
export function contexteRepartition(
  mois: string,
  versions: readonly VersionGrille[],
  beneficiaires: readonly BeneficiaireSaisie[],
): ContexteRepartition {
  return {
    mois,
    regles: versions.map((v) => ({
      id: v.id,
      service: v.service,
      depuisMois: v.depuisMois,
      parts: v.parts.map((p) => ({
        beneficiaireId: p.beneficiaireId,
        partPointsDeBase: p.partPointsDeBase,
      })),
    })),
    beneficiaires: beneficiaires.map((b) => ({
      id: b.id,
      libelle: b.libelle,
      recoitLeReste: b.recoitLeReste,
      galeriesExclues: b.galeriesExclues,
    })),
  };
}

interface Champs {
  date: string;
  service: string;
  saisie: SaisieMontant;
  montant: string;
  taux: string;
  payeur: string;
  galerieId: string;
  libelle: string;
}

function champsVides(date: string): Champs {
  return {
    date,
    service: '',
    saisie: 'ht',
    montant: '',
    taux: '20',
    payeur: '',
    galerieId: '',
    libelle: '',
  };
}

function champsDe(encaissement: Encaissement): Champs {
  return {
    date: encaissement.encaisseLe,
    service: encaissement.service,
    saisie: encaissement.saisie,
    montant: montantEnSaisie(
      encaissement.saisie === 'ttc' ? encaissement.ttcCentimes : encaissement.htCentimes,
    ),
    taux: pourcentageEnSaisie(encaissement.tauxTvaPointsDeBase),
    payeur: encaissement.payeur ?? '',
    galerieId: encaissement.galerieId ?? '',
    libelle: encaissement.libelle,
  };
}

const JOUR = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

/** La valeur « aucune galerie » du sélecteur : il n'admet pas de valeur vide. */
const SANS_GALERIE = 'aucune';

/** Le cumul HT d'un type face à son objectif, en une phrase. */
export function useObjectif(): (type: TypeRecetteVue, recuCentimes?: number) => string | null {
  const { t } = useTranslation('console');
  const f = useFormats();
  return (type, recuCentimes) => {
    if (type.objectifHtCentimes === null) return null;
    const recu = recuCentimes ?? type.encaisseHtCentimes ?? 0;
    const reste = type.objectifHtCentimes - recu;
    return reste > 0
      ? t('comptabilite.encaissements.objectif', {
          recu: f.euros(recu),
          objectif: f.euros(type.objectifHtCentimes),
          reste: f.euros(reste),
        })
      : t('comptabilite.encaissements.objectifAtteint', {
          recu: f.euros(recu),
          objectif: f.euros(type.objectifHtCentimes),
        });
  };
}

/**
 * Les encaissements (direction) : une somme reçue, saisie HT ou TTC, d'un type
 * de recette. L'APERÇU montre aussitôt HT, TVA, TTC et la part de chacun : il
 * est calculé par `repartirRecette` (`@artosera/shared`), la fonction même que
 * le serveur applique — l'écran n'invente rien, il annonce. Puis la liste du
 * mois affiché, chaque ligne avec la répartition que le serveur a calculée ;
 * elle se corrige ou se supprime tant que le mois est ouvert.
 */
export function Encaissements({
  mois,
  moisClotures,
  types,
  versions,
  beneficiaires,
  galeries,
  numero,
}: {
  /** Le mois affiché par la page. */
  mois: string;
  /** Les mois clôturés que la page connaît : on n'y enregistre rien. */
  moisClotures: ReadonlySet<string>;
  types: readonly TypeRecetteVue[];
  versions: readonly VersionGrille[];
  beneficiaires: readonly BeneficiaireSaisie[];
  galeries: readonly GalerieListe[];
  numero: string;
}): JSX.Element {
  const { t } = useTranslation('console');
  const f = useFormats();
  const elevation = useElevation();
  const nomType = useNomType();
  const phraseObjectif = useObjectif();
  const message = useMessageErreur('comptabilite');
  const relire = useRelireComptabilite();
  const id = useId();
  const liste = useEncaissements(mois, true);

  const dateParDefaut = (): string => {
    const jour = aujourdhui();
    return jour.startsWith(mois) ? jour : `${mois}-01`;
  };
  const [champs, setChamps] = useState<Champs>(() => champsVides(dateParDefaut()));
  const [enCorrection, setEnCorrection] = useState<Encaissement | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [fait, setFait] = useState<string | null>(null);
  const [aSupprimer, setASupprimer] = useState<string | null>(null);

  const changer = <K extends keyof Champs>(cle: K, valeur: Champs[K]): void => {
    setChamps((precedents) => ({ ...precedents, [cle]: valeur }));
    setErreur(null);
    setFait(null);
  };

  // Un type actif se choisit ; un type archivé ne garde que ses encaissements.
  const proposes = types.filter(
    (type) => type.actif || (enCorrection !== null && type.id === enCorrection.service),
  );
  const typeChoisi = types.find((type) => type.id === champs.service) ?? null;

  const montant = analyserMontant(champs.montant);
  const taux = analyserPourcentage(champs.taux);
  const montantInvalide = !montant.ok;
  const tauxInvalide = !taux.ok || taux.valeur === null;
  const dateValide = JOUR.test(champs.date);
  const moisDeLaDate = dateValide ? champs.date.slice(0, 7) : '';
  const moisFerme = moisDeLaDate !== '' && moisClotures.has(moisDeLaDate);

  // L'aperçu : la fonction même du serveur, sur la grille et le partage qu'il lit.
  const apercu = ((): {
    montants: ReturnType<typeof montantsRecette>;
    repartition: ReturnType<typeof repartirRecette>;
  } | null => {
    if (!montant.ok || montant.valeur === null || montant.valeur <= 0) return null;
    if (!taux.ok || taux.valeur === null || champs.service === '' || !dateValide) return null;
    const montants = montantsRecette(champs.saisie, montant.valeur, taux.valeur);
    const repartition = repartirRecette(
      {
        service: champs.service,
        htCentimes: montants.htCentimes,
        galerieId: champs.galerieId === '' ? null : champs.galerieId,
      },
      contexteRepartition(moisDeLaDate, versions, beneficiaires),
    );
    return { montants, repartition };
  })();

  // L'objectif du type : ce qui est déjà reçu, sans la ligne qu'on corrige.
  const dejaRecu =
    typeChoisi?.encaisseHtCentimes === null || typeChoisi === null
      ? 0
      : typeChoisi.encaisseHtCentimes -
        (enCorrection !== null && enCorrection.service === typeChoisi.id
          ? enCorrection.htCentimes
          : 0);
  const apresEncaissement = dejaRecu + (apercu?.montants.htCentimes ?? 0);
  const depassement =
    typeChoisi?.objectifHtCentimes != null && apercu !== null
      ? apresEncaissement - typeChoisi.objectifHtCentimes
      : 0;

  const peutEnregistrer = apercu !== null && !moisFerme && !enCours;

  const recommencer = (): void => {
    setChamps(champsVides(dateParDefaut()));
    setEnCorrection(null);
  };

  const enregistrer = async (evenement: FormEvent): Promise<void> => {
    evenement.preventDefault();
    if (!peutEnregistrer || !montant.ok || montant.valeur === null || !taux.ok) return;
    setEnCours(true);
    setErreur(null);
    setFait(null);
    const corps = {
      encaisseLe: champs.date,
      saisie: champs.saisie,
      montantCentimes: montant.valeur,
      tauxTvaPointsDeBase: taux.valeur,
      service: champs.service,
      payeur: champs.payeur.trim() === '' ? null : champs.payeur.trim(),
      galerieId: champs.galerieId === '' ? null : champs.galerieId,
      libelle: champs.libelle.trim() === '' ? null : champs.libelle.trim(),
    };
    try {
      const resultat = enCorrection
        ? await modifierEncaissement(enCorrection.id, corps)
        : await creerEncaissement(corps);
      setFait(
        t('comptabilite.encaissements.enregistre', { montant: f.euros(resultat.htCentimes) }),
      );
      recommencer();
    } catch (echec) {
      setErreur(message(echec));
    } finally {
      setEnCours(false);
      await relire();
    }
  };

  const supprimer = async (encaissement: Encaissement): Promise<void> => {
    setEnCours(true);
    setErreur(null);
    setFait(null);
    try {
      await elevation.executer(() => supprimerEncaissement(encaissement.id));
      if (enCorrection?.id === encaissement.id) recommencer();
    } catch (echec) {
      if (!estElevationAnnulee(echec)) setErreur(message(echec));
    } finally {
      setASupprimer(null);
      setEnCours(false);
      await relire();
    }
  };

  const donnees = liste.data;
  const fige = donnees?.statut === 'cloture' || moisClotures.has(mois);
  const objectifs = types.filter((type) => !type.base && type.objectifHtCentimes !== null);
  const nomPartie = (beneficiaireId: string, libelle: string): string =>
    beneficiaires.find((b) => b.id === beneficiaireId)?.libelle ?? libelle;

  return (
    <Section
      id="encaissements"
      numero={numero}
      titre={t('comptabilite.encaissements.titre')}
      note={t('comptabilite.encaissements.note')}
    >
      <p className={styles.clotureTexte}>{t('comptabilite.encaissements.texte')}</p>

      {objectifs.length > 0 ? (
        <ul className={styles.objectifs}>
          {objectifs.map((type) => (
            <li key={type.id}>
              <span className={styles.serviceNom}>{nomType(type.id)}</span>
              <span>{phraseObjectif(type)}</span>
            </li>
          ))}
        </ul>
      ) : null}

      <form
        className={styles.encaissementForm}
        aria-labelledby={`${id}-titre`}
        onSubmit={(evenement) => void enregistrer(evenement)}
        noValidate
      >
        <h3 id={`${id}-titre`} className={styles.exclusionsTitre}>
          {enCorrection
            ? t('comptabilite.encaissements.formulaireModif', {
                date: f.date(`${enCorrection.encaisseLe}T12:00:00`),
              })
            : t('comptabilite.encaissements.formulaire')}
        </h3>
        <div className={styles.encaissementChamps}>
          <div>
            <label className={styles.etiquetteChamp} htmlFor={`${id}-date`}>
              {t('comptabilite.encaissements.date')}
            </label>
            <Input
              id={`${id}-date`}
              type="date"
              mono
              required
              value={champs.date}
              onChange={(e) => changer('date', e.target.value)}
            />
          </div>
          <div>
            <label className={styles.etiquetteChamp} htmlFor={`${id}-type`}>
              {t('comptabilite.encaissements.type')}
            </label>
            <Select
              id={`${id}-type`}
              placeholder={t('comptabilite.encaissements.choisirType')}
              options={proposes.map((type) => ({ value: type.id, label: nomType(type.id) }))}
              value={champs.service}
              onValueChange={(valeur) => changer('service', valeur)}
            />
          </div>
          <div>
            <label className={styles.etiquetteChamp} htmlFor={`${id}-montant`}>
              {t('comptabilite.encaissements.montant')}
            </label>
            <span className={styles.avecSuffixe}>
              <Input
                id={`${id}-montant`}
                className={styles.champMontant}
                inputMode="decimal"
                autoComplete="off"
                mono
                required
                aria-invalid={montantInvalide || undefined}
                aria-describedby={montantInvalide ? `${id}-montant-erreur` : undefined}
                value={champs.montant}
                onChange={(e) => changer('montant', e.target.value)}
              />
              <span className={styles.suffixe} aria-hidden="true">
                €
              </span>
            </span>
            {montantInvalide ? (
              <p id={`${id}-montant-erreur`} className={styles.erreurChamp}>
                {t('comptabilite.encaissements.montantInvalide')}
              </p>
            ) : null}
          </div>
          <fieldset className={styles.saisie}>
            <legend className={styles.etiquetteChamp}>
              {t('comptabilite.encaissements.saisie')}
            </legend>
            {(['ht', 'ttc'] as const).map((valeur) => (
              <label key={valeur} className={styles.saisieChoix}>
                <input
                  type="radio"
                  name={`${id}-saisie`}
                  value={valeur}
                  checked={champs.saisie === valeur}
                  onChange={() => changer('saisie', valeur)}
                />
                {t(`comptabilite.encaissements.${valeur}`)}
              </label>
            ))}
          </fieldset>
          <div>
            <label className={styles.etiquetteChamp} htmlFor={`${id}-taux`}>
              {t('comptabilite.encaissements.taux')}
            </label>
            <span className={styles.avecSuffixe}>
              <Input
                id={`${id}-taux`}
                className={styles.champPourcent}
                inputMode="decimal"
                autoComplete="off"
                mono
                aria-invalid={tauxInvalide || undefined}
                aria-describedby={tauxInvalide ? `${id}-taux-erreur` : undefined}
                value={champs.taux}
                onChange={(e) => changer('taux', e.target.value)}
              />
              <span className={styles.suffixe} aria-hidden="true">
                %
              </span>
            </span>
            {tauxInvalide ? (
              <p id={`${id}-taux-erreur`} className={styles.erreurChamp}>
                {t('comptabilite.encaissements.tauxInvalide')}
              </p>
            ) : null}
          </div>
          <div>
            <label className={styles.etiquetteChamp} htmlFor={`${id}-payeur`}>
              {t('comptabilite.encaissements.payeur')}
            </label>
            <Input
              id={`${id}-payeur`}
              autoComplete="off"
              maxLength={120}
              aria-describedby={`${id}-payeur-aide`}
              value={champs.payeur}
              onChange={(e) => changer('payeur', e.target.value)}
            />
            <p id={`${id}-payeur-aide`} className={styles.aideChamp}>
              {t('comptabilite.encaissements.payeurAide')}
            </p>
          </div>
          <div>
            <label className={styles.etiquetteChamp} htmlFor={`${id}-galerie`}>
              {t('comptabilite.encaissements.galerie')}
            </label>
            <Select
              id={`${id}-galerie`}
              options={[
                { value: SANS_GALERIE, label: t('comptabilite.encaissements.sansGalerie') },
                ...galeries.filter((g) => !g.estTest).map((g) => ({ value: g.id, label: g.nom })),
              ]}
              value={champs.galerieId === '' ? SANS_GALERIE : champs.galerieId}
              onValueChange={(valeur) =>
                changer('galerieId', valeur === SANS_GALERIE ? '' : valeur)
              }
            />
          </div>
          <div className={styles.champLarge}>
            <label className={styles.etiquetteChamp} htmlFor={`${id}-libelle`}>
              {t('comptabilite.encaissements.libelle')}
            </label>
            <Input
              id={`${id}-libelle`}
              autoComplete="off"
              maxLength={160}
              placeholder={typeChoisi ? nomType(typeChoisi.id) : ''}
              aria-describedby={`${id}-libelle-aide`}
              value={champs.libelle}
              onChange={(e) => changer('libelle', e.target.value)}
            />
            <p id={`${id}-libelle-aide`} className={styles.aideChamp}>
              {t('comptabilite.encaissements.libelleAide')}
            </p>
          </div>
        </div>

        <div
          className={styles.apercuEncaissement}
          role="region"
          aria-label={t('comptabilite.encaissements.apercu')}
          aria-live="polite"
        >
          <h4 className={styles.apercuTitre}>{t('comptabilite.encaissements.apercu')}</h4>
          {apercu === null ? (
            <p className={commun.note}>{t('comptabilite.encaissements.apercuVide')}</p>
          ) : (
            <>
              <dl className={styles.apercuMontants}>
                <div>
                  <dt>{t('comptabilite.encaissements.apercuHt')}</dt>
                  <dd>{f.euros(apercu.montants.htCentimes)}</dd>
                </div>
                <div>
                  <dt>
                    {t('comptabilite.encaissements.apercuTva', {
                      taux: f.pourcentDePoints(taux.ok ? (taux.valeur ?? 0) : 0),
                    })}
                  </dt>
                  <dd>{f.euros(apercu.montants.tvaCentimes)}</dd>
                </div>
                <div>
                  <dt>{t('comptabilite.encaissements.apercuTtc')}</dt>
                  <dd>{f.euros(apercu.montants.ttcCentimes)}</dd>
                </div>
              </dl>
              {apercu.repartition.reparti ? (
                <ul className={styles.apercuParts}>
                  {apercu.repartition.parts.map((part) => (
                    <li key={part.beneficiaireId}>
                      <span>{nomPartie(part.beneficiaireId, part.libelle)}</span>
                      <span className={commun.celluleMuette}>
                        {f.pourcentDePoints(part.partPointsDeBase)}
                      </span>
                      <span className={styles.apercuMontant}>{f.euros(part.montantCentimes)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={styles.avertissement}>
                  {apercu.repartition.regle === null
                    ? t('comptabilite.encaissements.sansGrille', {
                        type: nomType(champs.service),
                        mois: f.moisLong(moisDeLaDate),
                      })
                    : t('comptabilite.encaissements.sansReste')}{' '}
                  <a className={commun.lien} href="#pourcentages">
                    {t('comptabilite.encaissements.reglerGrille')}
                  </a>
                </p>
              )}
              {typeChoisi?.objectifHtCentimes != null ? (
                <p className={depassement > 0 ? styles.avertissement : commun.note}>
                  {t('comptabilite.encaissements.objectifApres', {
                    recu: f.euros(apresEncaissement),
                    objectif: f.euros(typeChoisi.objectifHtCentimes),
                  })}
                  {depassement > 0
                    ? ` ${t('comptabilite.encaissements.objectifDepasse', {
                        ecart: f.euros(depassement),
                      })}`
                    : ''}
                </p>
              ) : null}
            </>
          )}
          {moisFerme ? (
            <p className={styles.avertissement}>
              {t('comptabilite.encaissements.moisCloture', { mois: f.moisLong(moisDeLaDate) })}
            </p>
          ) : null}
        </div>

        <div className={styles.actions}>
          <Button type="submit" loading={enCours} disabled={!peutEnregistrer}>
            {enCorrection
              ? t('comptabilite.encaissements.enregistrerModif')
              : t('comptabilite.encaissements.enregistrer')}
          </Button>
          {enCorrection ? (
            <Button type="button" variant="secondary" disabled={enCours} onClick={recommencer}>
              {t('comptabilite.encaissements.annuler')}
            </Button>
          ) : null}
        </div>
        {erreur ? (
          <p className={styles.erreur} role="alert">
            {erreur}
          </p>
        ) : null}
        {fait ? (
          <p className={styles.succes} role="status">
            {fait}
          </p>
        ) : null}
      </form>

      <h3 className={styles.exclusionsTitre}>
        {t('comptabilite.encaissements.liste', { mois: f.moisLong(mois) })}
      </h3>
      {fige ? <p className={commun.note}>{t('comptabilite.encaissements.fige')}</p> : null}
      {donnees === undefined ? null : donnees.items.length === 0 ? (
        <p className={commun.note}>
          {t('comptabilite.encaissements.vide', { mois: f.moisLong(mois) })}
        </p>
      ) : (
        <ul className={styles.encaissementsListe}>
          {donnees.items.map((encaissement) => {
            const date = f.date(`${encaissement.encaisseLe}T12:00:00`);
            const montant = f.euros(encaissement.htCentimes);
            return (
              <li key={encaissement.id} className={styles.encaissement}>
                <div className={styles.encaissementTete}>
                  <span className={styles.serviceNom}>{nomType(encaissement.service)}</span>
                  <span className={commun.celluleMuette}>{date}</span>
                </div>
                <p className={styles.encaissementLibelle}>
                  {[encaissement.payeur, encaissement.libelle].filter(Boolean).join(' · ')}
                </p>
                <dl className={styles.apercuMontants}>
                  <div>
                    <dt>{t('comptabilite.encaissements.apercuHt')}</dt>
                    <dd>{montant}</dd>
                  </div>
                  <div>
                    <dt>
                      {t('comptabilite.encaissements.apercuTva', {
                        taux: f.pourcentDePoints(encaissement.tauxTvaPointsDeBase),
                      })}
                    </dt>
                    <dd>{f.euros(encaissement.tvaCentimes)}</dd>
                  </div>
                  <div>
                    <dt>
                      {t('comptabilite.encaissements.apercuTtc')}
                      {encaissement.saisie === 'ttc'
                        ? ` (${t('comptabilite.encaissements.saisieTtc')})`
                        : ''}
                    </dt>
                    <dd>{f.euros(encaissement.ttcCentimes)}</dd>
                  </div>
                </dl>
                {encaissement.etatRepartition === 'exclue_espace_de_test' ? (
                  // La galerie est un espace de test : hors des comptes du mois.
                  <p className={commun.note}>
                    {t('comptabilite.encaissements.exclueEspaceDeTest')}
                  </p>
                ) : encaissement.repartition === null ? (
                  // Une clôture antérieure à la répartition figée de chaque
                  // encaissement : aucune part recalculée, celles du mois font foi.
                  <p className={commun.note}>{t('comptabilite.encaissements.figeeNonDetaillee')}</p>
                ) : encaissement.repartition.reparti ? (
                  <ul className={styles.apercuParts}>
                    {encaissement.repartition.parts.map((part) => (
                      <li key={part.beneficiaireId}>
                        <span>{nomPartie(part.beneficiaireId, part.libelle)}</span>
                        <span className={commun.celluleMuette}>
                          {f.pourcentDePoints(part.partPointsDeBase)}
                        </span>
                        <span className={styles.apercuMontant}>
                          {f.euros(part.montantCentimes)}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className={styles.avertissement}>
                    {t('comptabilite.encaissements.nonReparti')}
                  </p>
                )}
                {fige ? null : aSupprimer === encaissement.id ? (
                  <div className={styles.actions} role="group">
                    <span>{t('comptabilite.encaissements.confirmerSuppression')}</span>
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={enCours}
                      onClick={() => void supprimer(encaissement)}
                    >
                      {t('comptabilite.encaissements.confirmer')}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={enCours}
                      onClick={() => setASupprimer(null)}
                    >
                      {t('comptabilite.encaissements.annuler')}
                    </Button>
                  </div>
                ) : (
                  <div className={styles.actions}>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={enCours}
                      aria-label={t('comptabilite.encaissements.modifierAria', { date, montant })}
                      onClick={() => {
                        setEnCorrection(encaissement);
                        setChamps(champsDe(encaissement));
                        setErreur(null);
                        setFait(null);
                      }}
                    >
                      {t('comptabilite.encaissements.modifier')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={enCours}
                      aria-label={t('comptabilite.encaissements.supprimerAria', { date, montant })}
                      onClick={() => setASupprimer(encaissement.id)}
                    >
                      {t('comptabilite.encaissements.supprimer')}
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {elevation.dialogue}
    </Section>
  );
}
